import CustomContentBlockSection from '../components/CustomContentBlockSection';
import { registerSectionComponent } from '../config/sectionRegistry';

export const CUSTOM_PAGE_LANGUAGES = ['en', 'fr', 'es'];

const RESERVED_SLUGS = new Set([
  'account', 'admin', 'all-partners', 'auth', 'checkout',
  'chase-the-ace', 'chase-the-ace-card', 'chase-the-ace-purchase',
  'closed', 'code', 'confirm-email', 'contact', 'display', 'draws',
  'elimination-draw', 'excluded', 'facebook-email-required',
  'facebook-login-error', 'facebook-profile-notfound', 'faq',
  'finalize-account', 'firewall-restricted', 'geo-blocked',
  'google-login-error', 'google-profile-notfound', 'how-to-play',
  'jackpot-test', 'member-exists', 'modified-5050', 'offline',
  'partner', 'password-reset', 'preview', 'privacy', 'progressive-5050',
  'prize-draw', 'purchase', 'purchase-complete', 'receipt',
  'renew-tickets', 'responsible-gaming', 'signup', 'square-game',
  'superball-bingo', 'terms', 'unsubscribe', 'winners',
]);

const registeredPageNames = new Set();

export function createPageName(slug) {
  return `custom_page_${slug.replace(/-/g, '_')}`;
}

export function slugifyPageTitle(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/['"]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64);
}

export function isReservedCustomPageSlug(slug) {
  return RESERVED_SLUGS.has(slug);
}

export function getLocalizedCustomPageTitle(page, language) {
  const titles = typeof page?.titles === 'string'
    ? (() => { try { return JSON.parse(page.titles); } catch { return {}; } })()
    : (page?.titles || {});
  return titles[language]
    || titles.en
    || Object.values(titles).find(Boolean)
    || page?.slug
    || 'Untitled page';
}

export function buildCustomPageContentSections(page, count = 6) {
  const pageName = page.pageName || createPageName(page.slug);
  return Array.from({ length: count }, (_, index) => ({
    id: `${pageName}_content_${index + 1}`,
    page_name: pageName,
    section_type: `${pageName}_content_block_${index + 1}`,
    section_order: index + 1,
    is_visible: false,
    config: {
      contentKey: `customPages.${page.slug}.content.${index + 1}`,
      label: `Blank Content ${index + 1}`,
      background: '#ffffff',
      textColor: '#111827',
      textSize: 'base',
      height: 'auto',
    },
  }));
}

export function seedCustomPageSections(page) {
  try {
    const pageName = page.pageName || createPageName(page.slug);
    const stored = JSON.parse(localStorage.getItem('poc_page_sections') || '{}');
    if (stored[pageName]?.length > 0) return;
    stored[pageName] = buildCustomPageContentSections(page);
    localStorage.setItem('poc_page_sections', JSON.stringify(stored));
  } catch (error) {
    console.error('Error seeding custom page sections:', error);
  }
}

export function ensureCustomPageSectionRegistrations(pages = []) {
  pages.forEach(page => {
    const pageName = page.pageName || createPageName(page.slug);
    if (registeredPageNames.has(pageName)) return;
    registeredPageNames.add(pageName);

    buildCustomPageContentSections(page).forEach(section => {
      registerSectionComponent(section.section_type, CustomContentBlockSection, {
        category: 'content',
        label: section.config.label,
        description: 'Blank editable content section for this custom page',
        icon: '📝',
        gameTypes: '*',
        defaultConfig: section.config,
        supportedPages: [pageName],
      });
    });
  });
}

/**
 * Build a DTO payload to send to the API from the admin form draft.
 */
export function buildCustomPageDto({ id, slug, pageName, titles, enabled, order }) {
  return {
    id: id || '',
    slug,
    pageName: pageName || createPageName(slug),
    titles: JSON.stringify(titles),
    enabled: enabled !== false,
    sortOrder: order || 0,
  };
}

